import type { OwnerTransferPending } from '@common/api/featureService';

type MaybeOwnerTransferEnvelope = {
    data?: unknown;
};

const OWNER_TRANSFER_REFRESH_GRACE_MS = 1_000;
const MAX_BROWSER_TIMEOUT_MS = 2_147_483_647;

const unwrapPendingOwnerTransfer = (
    pending: OwnerTransferPending | MaybeOwnerTransferEnvelope | null | undefined
): unknown => {
    if (pending && typeof pending === 'object' && 'data' in pending) {
        return pending.data;
    }

    return pending;
};

const parseOwnerTransferExpiry = (expiresAt?: string | null): number | null => {
    if (!expiresAt) return null;

    const expiryTime = Date.parse(expiresAt);
    return Number.isFinite(expiryTime) ? expiryTime : null;
};

export const isPendingOwnerTransferExpired = (expiresAt?: string | null): boolean => {
    const expiryTime = parseOwnerTransferExpiry(expiresAt);
    return expiryTime !== null && expiryTime <= Date.now();
};

export const getPendingOwnerTransferRefreshDelay = (expiresAt?: string | null): number | null => {
    const expiryTime = parseOwnerTransferExpiry(expiresAt);
    if (expiryTime === null) return null;

    const delay = expiryTime - Date.now() + OWNER_TRANSFER_REFRESH_GRACE_MS;
    return Math.min(Math.max(delay, 0), MAX_BROWSER_TIMEOUT_MS);
};

export const normalizePendingOwnerTransfer = (
    pending: OwnerTransferPending | MaybeOwnerTransferEnvelope | null | undefined,
    workspaceId?: string
): OwnerTransferPending | null => {
    const candidate = unwrapPendingOwnerTransfer(pending);

    if (!candidate || typeof candidate !== 'object') {
        return null;
    }

    const record = candidate as Partial<OwnerTransferPending>;

    if (
        typeof record.requestId !== 'string' ||
        typeof record.workspaceId !== 'string' ||
        typeof record.previousOwnerId !== 'string' ||
        typeof record.targetUserId !== 'string' ||
        record.requestId.trim() === '' ||
        record.workspaceId.trim() === '' ||
        record.previousOwnerId.trim() === '' ||
        record.targetUserId.trim() === ''
    ) {
        return null;
    }

    if (record.status !== 'PENDING') {
        return null;
    }

    if (record.valid === false) {
        return null;
    }

    if (isPendingOwnerTransferExpired(record.expiresAt)) {
        return null;
    }

    if (workspaceId && record.workspaceId !== workspaceId) {
        return null;
    }

    return record as OwnerTransferPending;
};
